import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "./cn";
import { sectionCardClass } from "./section-card";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap gap-2 rounded-md text-md font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        primary:
          "bg-primary border-primary border text-primary-foreground hover:bg-primary/90 hover:border-primary/90",
        destructive:
          "border border-destructive text-destructive hover:bg-background hover:border-destructive hover:text-destructive",
        outline:
          "border border-primary text-primary bg-transparent hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary border border-secondary text-secondary-foreground hover:bg-secondary-foreground hover:text-secondary hover:border-secondary-foreground",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        // 회색 글자 ghost — 표·목록 줄 안의 보조 아이콘(정렬·펼치기·메뉴)과 머리말 보조 버튼.
        "ghost-muted": "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
        // 회색 글자 ghost 인데 올리면 삭제색 — 줄 지우기·항목 빼기 아이콘.
        "ghost-destructive": "text-muted-foreground hover:bg-accent hover:text-destructive",
        link: "text-primary underline-offset-4 hover:underline",
        // 회색 글자 링크 — 「무시한 n개 다시 보기」 같은 보조 글자 버튼.
        "link-muted": "text-muted-foreground underline-offset-4 hover:underline",
        // 삭제색 링크 — 「저장 실패 — 다시 시도」 같은 오류 줄 안의 글자 버튼.
        "link-destructive": "text-destructive underline-offset-4 hover:underline",
        black:
          "bg-foreground border-black text-background hover:bg-primary hover:border-primary hover:text-primary-foreground",
        // 파란(primary) 바탕 위 반전 버튼 — 랜딩 가입 띠의 신청 버튼.
        inverse: "border border-card bg-card text-primary hover:border-accent hover:bg-accent",
        // 거르기 칩(size 를 비우면 size chip) — 켜짐은 토글 aria-pressed · 라디오 칩 aria-checked · FilterChips 링크 aria-current="page".
        chip: "rounded-full border border-border bg-card font-normal text-muted-foreground hover:bg-muted aria-pressed:border-primary aria-pressed:bg-primary aria-pressed:text-primary-foreground aria-checked:border-primary aria-checked:bg-primary aria-checked:text-primary-foreground aria-[current=page]:border-primary aria-[current=page]:bg-primary aria-[current=page]:text-primary-foreground",
        // 폭 전체 목록 줄 버튼(size 를 비우면 size row · 모바일 견적 공종 머리줄·항목 줄) — 고른 줄은 aria-current 로 칠한다.
        row: "rounded-none text-lg font-normal hover:bg-muted aria-[current=true]:bg-accent [&_svg]:size-6",
        // 누르는 카드 줄(size 를 비우면 size row · 모양은 sectionCardClass interactive 한 벌) — 단가 공종 접기·모바일 견적 표지 카드.
        card: cn(sectionCardClass({ padding: "none", interactive: true }), "text-lg font-normal [&_svg]:size-6"),
        "none-style": "rounded-none",
      },
      size: {
        default: "min-h-10 px-4 py-2 text-md",
        sm: "min-h-9 rounded-md px-3 py-1 text-sm",
        lg: "min-h-11 rounded-md px-8 py-2 text-lg",
        // 고객이 누르는 큰 신청 버튼(랜딩 신청·간편접수 제출) — lg 에 굵은 글자.
        cta: "min-h-11 rounded-md px-8 py-2 text-lg font-semibold",
        // 현장 오늘 화면의 큰 작업 버튼(일지 쓰기·사진 올리기) — 엄지로 누르는 56px 높이.
        field: "min-h-14 rounded-md px-4 py-2 text-lg",
        // 표·시트 머리줄 안의 낮은 글자 버튼(견적 시트 공종 머리줄 「줄」「조건」).
        compact: "min-h-7 rounded-md px-2 py-1 text-sm",
        // 줄 버튼·카드 줄 버튼의 안쪽 여백(variant row·card 와 같이 — 그 둘은 size 를 비우면 이것).
        row: "gap-x-2 px-3 py-2",
        // 거르기 칩 크기(variant chip 과 같이 — chip 은 size 를 비우면 이것).
        chip: "h-9 gap-x-1.5 px-3.5 text-sm",
        icon: "size-10",
        // 표·목록 줄 안의 아이콘 전용 24px(정렬 화살표·줄 삭제 등) — 줄 높이를 늘리지 않고 좁은 칸에서도 눌리지 않는다.
        "icon-xs": "tap-target relative size-6 shrink-0",
        // 링크·글자 버튼용 작은 글자(높이·여백 없음) — 본문 사이에 끼는 보조 글자 버튼.
        xs: "text-xs font-normal",
        // 폰 현장 화면의 작은 글자 버튼 — 보이는 크기는 xs, 누르는 영역은 tap-target 으로 40px(사유 적기·앨범에서 고르기).
        "xs-touch": "tap-target relative gap-1 text-xs font-normal [&_svg]:size-3.5",
        // sm 글자 줄 안에 끼는 글자 버튼(높이·여백 없음) — 「저장 실패 — 다시 시도」·「보기」.
        inline: "text-sm",
        "none-style": "",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
);

// 모양이 크기와 짝인 variant — size 를 안 주면 짝 size 로 그려 기본 size(min-h-10 px-4)에 모양이 깨지지 않게 한다.
const PAIRED_SIZE: Partial<Record<NonNullable<VariantProps<typeof buttonVariants>["variant"]>, VariantProps<typeof buttonVariants>["size"]>> = {
  chip: "chip",
  row: "row",
  card: "row",
};

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** 필로스에서는 GA 추적 때문에 필수였다. 빌드파이는 GA 를 안 붙여 선택으로 둔다. */
  "data-gtm-id"?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, onClick, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";

    const customOnClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
      // if (
      //   typeof window !== "undefined" &&
      //   window.dataLayer &&
      //   process.env.NODE_ENV === "production"
      // ) {
      //   window.dataLayer.push({
      //     event: "button_click",
      //     event_category: "User Engagement",
      //     event_action: "click",
      //     event_label: e.currentTarget.name || e.currentTarget.innerHTML.trim(),
      //     event_value: 1, // 클릭당 1점 부여
      //     non_interaction: false, // 사용자 상호작용으로 간주
      //     korean_time: dayjs().format("YYYY-MM-DD HH:mm:ss"), // 한국 형식
      //     timezone: "Asia/Seoul",
      //   });
      // }
      if (onClick) onClick(e);
    };
    return (
      <Comp
        ref={ref}
        className={cn(
          variant === "none-style"
            ? className
            : buttonVariants({
                variant,
                size: size ?? (variant ? PAIRED_SIZE[variant] : undefined),
                className,
              })
        )}
        // asChild 면 자식(Link·a)에 type 을 붙이지 않는다 — <a type="button"> 은 잘못된 속성이다.
        type={asChild ? undefined : "button"}
        {...props}
        onClick={customOnClick}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
